'use client';

import { Icons } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { Building2, XIcon } from 'lucide-react';
import { BusinessEntityCardProps } from './types';

/**
 * BusinessEntityCard Component
 *
 * Displays a selected business entity as a card with:
 * - Entity name with building icon
 * - Edit button (pencil icon) - toggles inline field expansion
 * - Remove button (X icon)
 * - Error state indicator for missing fields
 * - "New" badge for newly created entities
 */
export const BusinessEntityCard = ({
  entity,
  onRemove,
  onToggleExpand,
  isExpanded,
  disabled = false,
}: BusinessEntityCardProps) => {
  // Only show error state when collapsed AND has missing fields
  // When expanded, the user is actively editing, so don't show error border
  const showErrorState = entity.hasMissingFields && !isExpanded;

  return (
    <div
      className={cn(
        'p-3 border rounded-md bg-bg-primary transition-colors',
        showErrorState ? 'border-border-error' : 'border-border-primary',
        isExpanded ? 'border-b-0 rounded-b-none' : ''
      )}
    >
      <div className="flex justify-between items-center">
        <div className="flex gap-2 items-center">
          {showErrorState ? (
            <div className="h-5 w-5 rounded-full bg-bg-error-subtle flex items-center justify-center">
              <span className="text-text-error text-xs">!</span>
            </div>
          ) : (
            <Building2 className="text-fg-quinary h-5 w-5" />
          )}
          <div>
            <h4 className="text-text-primary txt-small-medium">
              {entity.displayName}
            </h4>
            {entity.isNew && (
              <span className="text-xs text-text-tertiary">
                New Business Entity
              </span>
            )}
          </div>
        </div>

        <div className="flex gap-1 items-center">
          {/* Edit/Expand Button */}
          <button
            type="button"
            onClick={() => onToggleExpand(entity.id)}
            disabled={disabled}
            className={cn(
              'p-2 rounded-sm transition-colors',
              isExpanded
                ? 'bg-bg-brand-subtle text-fg-brand'
                : 'hover:bg-bg-secondary-hover',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            title={isExpanded ? 'Collapse fields' : 'Edit fields'}
          >
            <Icons.Pencil02
              className={cn(
                'h-5 w-5',
                isExpanded
                  ? 'text-fg-brand'
                  : 'text-button-tertiary-fg hover:text-button-tertiary-fg-hover',
                disabled && 'cursor-not-allowed'
              )}
            />
          </button>

          {/* Remove Button */}
          <button
            type="button"
            onClick={() => onRemove(entity.id)}
            disabled={disabled}
            className={cn(
              'p-2 hover:bg-bg-secondary-hover rounded-sm transition-colors',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            title="Remove"
          >
            <XIcon
              className={cn(
                'h-5 w-5 text-button-tertiary-fg hover:text-button-tertiary-fg-hover',
                disabled && 'cursor-not-allowed'
              )}
            />
          </button>
        </div>
      </div>

      {/* Missing fields indicator */}
      {showErrorState && (
        <p className="mt-1 ml-7 text-xs text-text-error">
          {entity.isNew
            ? 'Click edit to fill required fields'
            : 'Missing required information - click edit to view details'}
        </p>
      )}
    </div>
  );
};
